import {
  DEFAULT_DESCRIPTION,
  DEFAULT_SITE_SETTINGS,
  DEFAULT_TITLE,
  PRIVATE_PAGE_TITLES,
  normalizePath,
} from "./seoDefaults";
import { ResolvedSeo, SeoPageConfig, SeoPublicConfig, SeoSiteSettings } from "./types";

/**
 * Writes the SEO head tags. There is no helmet style dependency in this app, so the head is
 * managed directly - every tag is created once and then updated in place, which keeps the static
 * tags from `public/index.html` alive and avoids piling up duplicates on a client side route
 * change.
 */

const MANAGED_OG = [
  "og:title",
  "og:description",
  "og:url",
  "og:type",
  "og:image",
  "og:site_name",
  "og:locale",
];

function upsertMeta(attribute: "name" | "property", key: string, content: string | null) {
  const head = document.head;
  let element = head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);

  if (!content) {
    element?.remove();
    return;
  }

  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    head.appendChild(element);
  }

  element.setAttribute("content", content);
}

function upsertLink(rel: string, href: string | null) {
  const head = document.head;
  let element = head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);

  if (!href) {
    element?.remove();
    return;
  }

  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    head.appendChild(element);
  }

  element.setAttribute("href", href);
}

function upsertJsonLd(id: string, data: unknown | null) {
  const head = document.head;
  let element = head.querySelector<HTMLScriptElement>(`script[data-seo-jsonld="${id}"]`);

  if (data === null) {
    element?.remove();
    return;
  }

  if (!element) {
    element = document.createElement("script");
    element.type = "application/ld+json";
    element.setAttribute("data-seo-jsonld", id);
    head.appendChild(element);
  }

  element.textContent = JSON.stringify(data);
}

function joinUrl(baseUrl: string, path: string): string {
  const base = (baseUrl || "").trim().replace(/\/+$/, "");
  if (!path || path === "/") return `${base}/`;
  return `${base}${path.startsWith("/") ? path : `/${path}`}`;
}

function twitterProfileUrl(handle: string): string | null {
  const cleaned = handle.trim().replace(/^@/, "");
  if (!cleaned) return null;
  return `https://twitter.com/${cleaned}`;
}

function findPage(pages: SeoPageConfig[], path: string): SeoPageConfig | null {
  return pages.find((page) => normalizePath(page.path || "") === path) || null;
}

function privateTitle(path: string, fallback: string): string {
  const match = Object.keys(PRIVATE_PAGE_TITLES).find(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`) || path.startsWith(prefix),
  );
  return match ? PRIVATE_PAGE_TITLES[match] : fallback;
}

/**
 * Turns the configuration and the current path into the tag values. A path that is not in the
 * registry resolves to a non indexable result - that is the safe answer for a dashboard route that
 * was added without telling the SEO module about it.
 */
export function resolveSeo(config: SeoPublicConfig, pathname: string): ResolvedSeo {
  const site: SeoSiteSettings = { ...DEFAULT_SITE_SETTINGS, ...(config?.site || {}) };
  const pages = config?.pages || [];
  const path = normalizePath(pathname);
  const page = findPage(pages, path);

  const siteIndexingEnabled = site.indexingEnabled !== false;
  const indexable = siteIndexingEnabled && page !== null && page.indexable !== false;

  const title = page?.title?.trim()
    ? page.title.trim()
    : indexable
      ? site.defaultTitle?.trim() || DEFAULT_TITLE
      : privateTitle(path, site.defaultTitle?.trim() || DEFAULT_TITLE);

  const description = page?.description?.trim()
    ? page.description.trim()
    : site.defaultDescription?.trim() || DEFAULT_DESCRIPTION;

  const robots = indexable
    ? site.extraRobotsRules?.trim() || "index, follow"
    : "noindex, nofollow";

  // A canonical link is only published on an indexable page. Pointing a noindex page at a
  // canonical URL would tell a crawler the page is a duplicate of another one, which is not what
  // "keep this out of the index" means.
  const canonical = indexable && page
    ? page.canonicalUrl?.trim() || joinUrl(site.baseUrl, page.path || path)
    : null;

  const ogImage = page?.ogImage?.trim() || site.ogImage?.trim() || null;

  return {
    title,
    description,
    robots,
    canonical,
    indexable,
    ogTitle: title,
    ogDescription: description,
    ogUrl: canonical || "",
    ogType: page?.ogType?.trim() || "website",
    ogImage,
    ogSiteName: site.siteName || DEFAULT_SITE_SETTINGS.siteName,
    ogLocale: site.locale || DEFAULT_SITE_SETTINGS.locale,
    twitterCard: ogImage ? "summary_large_image" : "summary",
    twitterTitle: title,
    twitterDescription: description,
    twitterImage: ogImage,
    twitterSite: twitterProfileUrl(site.twitterHandle || ""),
  };
}

function buildOrganization(site: SeoSiteSettings) {
  const baseUrl = (site.baseUrl || "").trim().replace(/\/+$/, "");
  const organization: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: site.siteName,
    url: `${baseUrl}/`,
  };

  if (site.organizationLegalName?.trim()) organization.legalName = site.organizationLegalName.trim();
  if (site.organizationDescription?.trim()) {
    organization.description = site.organizationDescription.trim();
  }
  if (site.organizationLogo?.trim()) organization.logo = site.organizationLogo.trim();

  const profile = twitterProfileUrl(site.twitterHandle || "");
  if (profile) organization.sameAs = [profile];

  return organization;
}

function buildWebSite(site: SeoSiteSettings) {
  const baseUrl = (site.baseUrl || "").trim().replace(/\/+$/, "");
  const website: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: site.siteName,
    url: `${baseUrl}/`,
  };

  if (site.defaultDescription?.trim()) website.description = site.defaultDescription.trim();
  const language = (site.locale || "").split("_")[0];
  if (language) website.inLanguage = language;

  return website;
}

function buildWebPage(page: SeoPageConfig, site: SeoSiteSettings, resolved: ResolvedSeo) {
  const baseUrl = (site.baseUrl || "").trim().replace(/\/+$/, "");
  const webPage: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: resolved.title,
    description: resolved.description,
    url: resolved.canonical,
    isPartOf: {
      "@type": "WebSite",
      name: site.siteName,
      url: `${baseUrl}/`,
    },
  };

  if (resolved.ogImage) webPage.primaryImageOfPage = resolved.ogImage;
  if (page.lastModified) webPage.dateModified = page.lastModified;

  return webPage;
}

function buildPageJsonLd(
  page: SeoPageConfig,
  site: SeoSiteSettings,
  resolved: ResolvedSeo,
): Record<string, unknown> | null {
  const raw = page.structuredDataJson?.trim();
  if (raw) {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return { "@context": "https://schema.org", ...parsed };
      }
    } catch {
      console.warn("SEO Setup: the structured data for this page is not valid JSON, so it was skipped.");
    }
    return null;
  }

  if (page.structuredDataType === "WEB_PAGE") {
    return buildWebPage(page, site, resolved);
  }

  return null;
}

/**
 * Applies the resolved tags to the document head.
 */
export function applySeo(config: SeoPublicConfig, pathname: string): ResolvedSeo {
  const site: SeoSiteSettings = { ...DEFAULT_SITE_SETTINGS, ...(config?.site || {}) };
  const resolved = resolveSeo(config, pathname);
  const page = findPage(config?.pages || [], normalizePath(pathname));

  document.title = resolved.title;

  upsertMeta("name", "description", resolved.description);
  upsertMeta("name", "robots", resolved.robots);
  upsertMeta("name", "google-site-verification", site.googleSiteVerification?.trim() || null);
  upsertLink("canonical", resolved.canonical);

  upsertMeta("property", "og:title", resolved.ogTitle);
  upsertMeta("property", "og:description", resolved.ogDescription);
  upsertMeta("property", "og:url", resolved.ogUrl || null);
  upsertMeta("property", "og:type", resolved.ogType);
  upsertMeta("property", "og:image", resolved.ogImage);
  upsertMeta("property", "og:site_name", resolved.ogSiteName);
  upsertMeta("property", "og:locale", resolved.ogLocale);

  upsertMeta("name", "twitter:card", resolved.twitterCard);
  upsertMeta("name", "twitter:title", resolved.twitterTitle);
  upsertMeta("name", "twitter:description", resolved.twitterDescription);
  upsertMeta("name", "twitter:image", resolved.twitterImage);
  upsertMeta("name", "twitter:site", resolved.twitterSite);

  // The structured data belongs to the pages that are actually published. On a private route it is
  // removed instead, so a dashboard URL never carries an Organization or WebSite schema.
  if (resolved.indexable && page) {
    upsertJsonLd("organization", buildOrganization(site));
    upsertJsonLd("website", buildWebSite(site));
    upsertJsonLd("page", buildPageJsonLd(page, site, resolved));
  } else {
    upsertJsonLd("organization", null);
    upsertJsonLd("website", null);
    upsertJsonLd("page", null);
  }

  return resolved;
}

export { MANAGED_OG };
